import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { db } from '../utils/firebase';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDocs, serverTimestamp } from 'firebase/firestore';
import { collectionPath } from '../utils/firestoreCollections';
import { logEntityChanges } from '../utils/logger';
import { sanitizeString } from '../utils/sanitization';
import { canViewAllData, FEATURE_PERMISSIONS } from '../utils/permissions';
import { usePermissions } from '../hooks/usePermissions';

// Calendar view modes
const VIEW_MODES = {
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day',
  AGENDA: 'agenda'
};

// Event type categories
const EVENT_TYPES = {
  trade_show: { name: 'Trade Show', icon: '🏪', color: '#E11D48', bgColor: '#FFE4E6' },
  market_week: { name: 'Market Week', icon: '📊', color: '#7C3AED', bgColor: '#EDE9FE' },
  customer_visit: { name: 'Customer Visit', icon: '🤝', color: '#0891B2', bgColor: '#CFFAFE' },
  internal_meeting: { name: 'Internal Meeting', icon: '👥', color: '#2563EB', bgColor: '#DBEAFE' },
  pto: { name: 'PTO / Out of Office', icon: '🌴', color: '#16A34A', bgColor: '#DCFCE7' },
  other: { name: 'Other', icon: '📌', color: '#6366F1', bgColor: '#EEF2FF' }
};

// Day names
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Helper to generate calendar grid
const generateCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPadding = firstDay.getDay();
  const totalDays = lastDay.getDate();
  
  const days = [];
  
  // Previous month padding
  const prevMonth = new Date(year, month, 0);
  for (let i = startPadding - 1; i >= 0; i--) {
    days.push({
      date: new Date(year, month - 1, prevMonth.getDate() - i),
      isCurrentMonth: false
    });
  }
  
  // Current month
  for (let i = 1; i <= totalDays; i++) {
    days.push({
      date: new Date(year, month, i),
      isCurrentMonth: true
    });
  }
  
  // Next month padding (fill to 42 days for 6 rows)
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false
    });
  }
  
  return days;
};

// Generate week days for week view
const generateWeekDays = (startDate) => {
  const days = [];
  const start = new Date(startDate);
  start.setDate(start.getDate() - start.getDay()); // Start from Sunday
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    days.push(date);
  }
  
  return days;
};

// Format date for display
const formatDate = (date) => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (date) => {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const isSameDay = (d1, d2) => {
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

// Format a compact time range string
const formatTimeRange = (event) => {
  if (event.allDay) return 'All day';
  if (!event.startTime) return '';
  const start = formatTime(new Date(event.startTime));
  if (!event.endTime) return start;
  const end = formatTime(new Date(event.endTime));
  return `${start} – ${end}`;
};

// Check if a date falls within an event's date range (inclusive)
const eventSpansDate = (event, date) => {
  const eventStart = new Date(event.startTime);
  if (isSameDay(eventStart, date)) return true;
  if (!event.endTime) return false;
  const eventEnd = new Date(event.endTime);
  // Check if date is between start and end (inclusive, comparing date only)
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const s = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
  const e = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
  return d >= s && d <= e;
};

// Check if an event spans multiple days
const isMultiDayEvent = (event) => {
  if (!event.startTime || !event.endTime) return false;
  const s = new Date(event.startTime);
  const e = new Date(event.endTime);
  return s.getFullYear() !== e.getFullYear() ||
         s.getMonth() !== e.getMonth() ||
         s.getDate() !== e.getDate();
};

// Format date range for multi-day events
const formatDateRange = (event) => {
  if (!event.startTime) return '';
  const s = new Date(event.startTime);
  const start = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (!event.endTime) return start;
  const e = new Date(event.endTime);
  const end = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${start} – ${end}`;
};

// Event Tooltip Component
const EventTooltip = ({ event, anchorRect, containerRef }) => {
  const eventType = EVENT_TYPES[event.eventType] || EVENT_TYPES.other;
  const tooltipRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRect || !tooltipRef.current || !containerRef?.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const tt = tooltipRef.current.getBoundingClientRect();
    let top = anchorRect.bottom - container.top + 4;
    let left = anchorRect.left - container.left;
    // Keep within container bounds
    if (left + tt.width > container.width) left = container.width - tt.width - 8;
    if (left < 0) left = 4;
    if (top + tt.height > container.height) top = anchorRect.top - container.top - tt.height - 4;
    setPos({ top, left });
  }, [anchorRect, containerRef]);

  return (
    <div
      ref={tooltipRef}
      className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-64 pointer-events-none"
      style={{ top: pos.top, left: pos.left }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: eventType.color }}
        />
        <span className="font-semibold text-gray-900 text-sm truncate">{event.title}</span>
      </div>
      <div className="space-y-1 text-xs text-gray-600">
        <div className="flex items-center gap-1.5">
          <span className="text-gray-400">📅</span>
          <span>{eventType.icon} {eventType.name}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-gray-400">🕐</span>
          <span>{isMultiDayEvent(event) ? formatDateRange(event) : formatTimeRange(event)}</span>
        </div>
        {event.location && (
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400">📍</span>
            <span className="truncate">{event.location}</span>
          </div>
        )}
        {event.attendees && (
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400">👥</span>
            <span className="truncate">{event.attendees}</span>
          </div>
        )}
        {event.userDisplayName && (
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400">👤</span>
            <span>{event.userDisplayName}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Event Card Component
const EventCard = ({ event, onClick, compact = false, spanning = false, spanCols = 1, containerRef }) => {
  const eventType = EVENT_TYPES[event.eventType] || EVENT_TYPES.other;
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const handleMouseEnter = () => {
    setHovered(true);
    if (cardRef.current) setAnchorRect(cardRef.current.getBoundingClientRect());
  };

  return (
    <>
      <div
        ref={cardRef}
        onClick={(e) => { e.stopPropagation(); onClick?.(event); }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHovered(false)}
        className={`${compact ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-sm'} rounded cursor-pointer truncate mb-0.5 transition-all duration-150 ${spanning ? 'relative z-10' : ''}`}
        style={{
          backgroundColor: hovered ? eventType.color : eventType.bgColor,
          color: hovered ? 'white' : 'inherit',
          borderLeft: spanning ? 'none' : `3px solid ${eventType.color}`,
          ...(spanning ? { borderRadius: '4px', gridColumn: `span ${spanCols}` } : {})
        }}
      >
        {compact ? (
          <div className="truncate">
            <span>{eventType.icon} {event.title}</span>
            {event.startTime && !event.allDay && !spanning && (
              <span className={`ml-1 ${hovered ? 'opacity-90' : 'opacity-60'}`}>{formatTime(new Date(event.startTime))}</span>
            )}
          </div>
        ) : (
          <>
            <div className="font-medium truncate">{eventType.icon} {event.title}</div>
            {event.startTime && (
              <div className={`text-xs ${hovered ? 'opacity-90' : 'opacity-75'}`}>
                {formatTimeRange(event)}
              </div>
            )}
          </>
        )}
      </div>
      {hovered && containerRef?.current && (
        <EventTooltip event={event} anchorRect={anchorRect} containerRef={containerRef} />
      )}
    </>
  );
};

// Generate 15-minute increment time options
const TIME_OPTIONS = (() => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
      options.push({ value, label });
    }
  }
  return options;
})();

// Snap a time string (HH:MM) to nearest 15-min slot
const snapToNearest15 = (timeStr) => {
  if (!timeStr) return '09:00';
  const [h, m] = timeStr.split(':').map(Number);
  const snapped = Math.round(m / 15) * 15;
  const newH = snapped === 60 ? h + 1 : h;
  const newM = snapped === 60 ? 0 : snapped;
  if (newH >= 24) return '23:45';
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
};

// Event Modal Component
const EventModal = ({ event, onClose, onSave, onDelete, isNew = false, isAdmin = false }) => {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    eventType: event?.eventType || 'other',
    startDate: event?.startTime ? new Date(event.startTime).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    startTime: event?.startTime ? snapToNearest15(new Date(event.startTime).toTimeString().slice(0, 5)) : '09:00',
    endDate: event?.endTime ? new Date(event.endTime).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    endTime: event?.endTime ? snapToNearest15(new Date(event.endTime).toTimeString().slice(0, 5)) : '10:00',
    allDay: event?.allDay || false,
    location: event?.location || '',
    attendees: event?.attendees?.join(', ') || '',
    visibility: event?.visibility || 'team',
    reminders: event?.reminders || []
  });
  const [saving, setSaving] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const startTime = form.allDay 
        ? new Date(`${form.startDate}T00:00:00`).toISOString()
        : new Date(`${form.startDate}T${form.startTime}`).toISOString();
      const endTime = form.allDay
        ? new Date(`${form.endDate}T23:59:59`).toISOString()
        : new Date(`${form.endDate}T${form.endTime}`).toISOString();
      
      await onSave({
        ...event,
        title: sanitizeString(form.title, 200),
        description: sanitizeString(form.description, 2000),
        eventType: form.eventType,
        startTime,
        endTime,
        allDay: form.allDay,
        location: sanitizeString(form.location, 500),
        attendees: form.attendees.split(',').map(a => a.trim()).filter(Boolean),
        visibility: form.visibility
      });
      onClose();
    } catch (err) {
      console.error('Error saving event:', err);
    } finally {
      setSaving(false);
    }
  };
  
  const isReadOnly = !onSave;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {isNew ? 'New Event' : (isReadOnly ? 'View Event' : 'Edit Event')}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Type *</label>
                <select
                  value={form.eventType}
                  onChange={(e) => setForm(f => ({ ...f, eventType: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  disabled={isReadOnly}
                >
                  {Object.entries(EVENT_TYPES).map(([key, config]) => (
                    <option key={key} value={key}>{config.icon} {config.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Event title"
                  required
                  disabled={isReadOnly}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="allDay"
                  checked={form.allDay}
                  onChange={(e) => setForm(f => ({ ...f, allDay: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 rounded"
                  disabled={isReadOnly}
                />
                <label htmlFor="allDay" className="text-sm text-gray-700">All-day event</label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                    disabled={isReadOnly}
                  />
                </div>
                {!form.allDay && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <select
                      value={form.startTime}
                      onChange={(e) => setForm(f => ({ ...f, startTime: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 bg-white"
                      disabled={isReadOnly}
                    >
                      {TIME_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm(f => ({ ...f, endDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                    disabled={isReadOnly}
                  />
                </div>
                {!form.allDay && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <select
                      value={form.endTime}
                      onChange={(e) => setForm(f => ({ ...f, endTime: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 bg-white"
                      disabled={isReadOnly}
                    >
                      {TIME_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Add location"
                  disabled={isReadOnly}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Add description"
                  disabled={isReadOnly}
                />
              </div>
              
              {!isReadOnly && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                  <select
                    value={form.visibility}
                    onChange={(e) => setForm(f => ({ ...f, visibility: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 bg-white"
                  >
                    {isAdmin ? (
                      <>
                        <option value="private">Private</option>
                        <option value="all_reps">Shared with All Reps</option>
                      </>
                    ) : (
                      <option value="private">Shared</option>
                    )}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    {isAdmin
                      ? form.visibility === 'all_reps' ? 'All sales reps will see this event' : 'Only visible to admins'
                      : 'Visible to you and admins'}
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attendees (comma-separated emails)</label>
                <input
                  type="text"
                  value={form.attendees}
                  onChange={(e) => setForm(f => ({ ...f, attendees: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="email1@example.com, email2@example.com"
                  disabled={isReadOnly}
                />
              </div>
            </div>
            
            <div className="flex justify-between mt-6 pt-4 border-t">
              {!isNew && !isReadOnly && (
                <button
                  type="button"
                  onClick={() => onDelete(event)}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Delete
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {isReadOnly ? 'Close' : 'Cancel'}
                </button>
                {!isReadOnly && (
                  <button
                    type="submit"
                    disabled={saving || !form.title}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Main Calendar Page Component
const CalendarPage = ({ user: propUser }) => {
  const { currentUser: authUser } = useAuth();
  // Effective user honors admin impersonation (propUser = impersonated / effective user).
  // Falls back to the authenticated user when no prop is provided.
  const currentUser = propUser || authUser;
  const { can } = usePermissions(currentUser);
  const isAdmin = canViewAllData(currentUser) || can(FEATURE_PERMISSIONS.CALENDAR_VIEW_ALL);
  
  // State
  const [viewMode, setViewMode] = useState(VIEW_MODES.MONTH);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [isNewEvent, setIsNewEvent] = useState(false);
  const [filterEventType, setFilterEventType] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [users, setUsers] = useState([]);
  
  // Fetch users (for admin filtering)
  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchUsers = async () => {
      try {
        const usersSnap = await getDocs(collection(db, collectionPath('users')));
        const usersData = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setUsers(usersData);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    
    fetchUsers();
  }, [isAdmin]);
  
  // Fetch calendar events
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    setLoading(true);
    
    // Calculate date range for query
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
    
    // Build query based on user role
    let eventsQuery;
    if (isAdmin && filterUser === 'all') {
      // Admin sees all events
      eventsQuery = query(
        collection(db, collectionPath('calendarEvents')),
        where('startTime', '>=', startOfMonth.toISOString()),
        where('startTime', '<=', endOfMonth.toISOString())
      );
    } else {
      // Non-admin or filtered: see own events + shared events (client-side RBAC filtering)
      eventsQuery = query(
        collection(db, collectionPath('calendarEvents')),
        where('startTime', '>=', startOfMonth.toISOString()),
        where('startTime', '<=', endOfMonth.toISOString())
      );
    }
    
    const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
      let allEvents = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Filter events based on visibility and user role
      allEvents = allEvents.filter(event => {
        // User's own events always visible
        if (event.userId === currentUser.uid) return true;
        
        // Admin/manager can see all events (with optional user filter)
        if (isAdmin) {
          if (filterUser !== 'all' && event.userId !== filterUser) return false;
          return true;
        }
        
        // Non-admin: see events explicitly shared with them OR shared with all reps
        if (event.adminSharedWith?.includes(currentUser.uid)) return true;
        if (event.visibility === 'all_reps') return true;
        
        return false;
      });
      
      // Filter by event type
      if (filterEventType !== 'all') {
        allEvents = allEvents.filter(e => e.eventType === filterEventType);
      }
      
      setEvents(allEvents);
      setLoading(false);
    }, (err) => {
      console.error('Error fetching events:', err);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [currentUser?.uid, currentDate, isAdmin, filterEventType, filterUser]);
  
  // Navigate to previous/next period
  const navigatePeriod = (direction) => {
    const newDate = new Date(currentDate);
    
    switch (viewMode) {
      case VIEW_MODES.MONTH:
        newDate.setMonth(newDate.getMonth() + direction);
        break;
      case VIEW_MODES.WEEK:
        newDate.setDate(newDate.getDate() + (direction * 7));
        break;
      case VIEW_MODES.DAY:
        newDate.setDate(newDate.getDate() + direction);
        break;
      default:
        newDate.setMonth(newDate.getMonth() + direction);
    }
    
    setCurrentDate(newDate);
  };
  
  // Go to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  // Create new event
  const handleNewEvent = (date = null) => {
    setSelectedEvent(date ? {
      startTime: new Date(date.setHours(9, 0, 0, 0)).toISOString(),
      endTime: new Date(date.setHours(10, 0, 0, 0)).toISOString()
    } : null);
    setIsNewEvent(true);
    setShowEventModal(true);
  };
  
  // Save event
  const handleSaveEvent = async (eventData) => {
    try {
      if (isNewEvent) {
        // Create new event
        const newEvent = {
          ...eventData,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userDisplayName: currentUser.displayName || `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          source: 'crm',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await addDoc(collection(db, collectionPath('calendarEvents')), newEvent);
        
        await logEntityChanges({
          oldObj: null,
          newObj: newEvent,
          fields: ['title', 'eventType', 'startTime', 'endTime'],
          entityType: 'CalendarEvent',
          entityId: 'new',
          user: currentUser,
          action: 'Created'
        });
      } else {
        // Update existing event
        const eventRef = doc(db, collectionPath('calendarEvents'), eventData.id);
        const updateData = {
          ...eventData,
          updatedAt: serverTimestamp()
        };
        delete updateData.id;
        
        await updateDoc(eventRef, updateData);
        
        await logEntityChanges({
          oldObj: selectedEvent,
          newObj: eventData,
          fields: ['title', 'eventType', 'startTime', 'endTime', 'description', 'location'],
          entityType: 'CalendarEvent',
          entityId: eventData.id,
          user: currentUser,
          action: 'Updated'
        });
      }
    } catch (err) {
      console.error('Error saving event:', err);
      throw err;
    }
  };
  
  // Delete event
  const handleDeleteEvent = async (event) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await deleteDoc(doc(db, collectionPath('calendarEvents'), event.id));
      
      await logEntityChanges({
        oldObj: event,
        newObj: null,
        fields: ['title'],
        entityType: 'CalendarEvent',
        entityId: event.id,
        user: currentUser,
        action: 'Deleted'
      });
      
      setShowEventModal(false);
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };
  
  // Get events for a specific date (including multi-day events that span this date)
  const getEventsForDate = useCallback((date) => {
    return events.filter(event => eventSpansDate(event, date))
      .sort((a, b) => {
        // All-day events first, then by start time
        if (a.allDay && !b.allDay) return -1;
        if (!a.allDay && b.allDay) return 1;
        return new Date(a.startTime) - new Date(b.startTime);
      });
  }, [events]);
  
  // Calendar grid for month view
  const calendarDays = useMemo(() => {
    return generateCalendarDays(currentDate.getFullYear(), currentDate.getMonth());
  }, [currentDate]);
  
  // Week days for week view
  const weekDays = useMemo(() => {
    return generateWeekDays(currentDate);
  }, [currentDate]);
  
  // Get period title
  const getPeriodTitle = () => {
    switch (viewMode) {
      case VIEW_MODES.MONTH:
        return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      case VIEW_MODES.WEEK:
        const start = weekDays[0];
        const end = weekDays[6];
        if (start.getMonth() === end.getMonth()) {
          return `${MONTHS[start.getMonth()]} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
        }
        return `${MONTHS[start.getMonth()]} ${start.getDate()} - ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
      case VIEW_MODES.DAY:
        return `${FULL_DAYS[currentDate.getDay()]}, ${MONTHS[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
      case VIEW_MODES.AGENDA:
        return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()} - Agenda`;
      default:
        return '';
    }
  };
  
  // Split calendar days into week rows for spanning event support
  const calendarWeeks = useMemo(() => {
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }
    return weeks;
  }, [calendarDays]);

  // Container ref for tooltip positioning
  const calendarContainerRef = useRef(null);

  // Calculate spanning multi-day events for a week row
  const getSpanningEventsForWeek = useCallback((weekDates) => {
    // Find multi-day events that overlap this week
    const spanning = events
      .filter(e => isMultiDayEvent(e) && weekDates.some(wd => eventSpansDate(e, wd.date)))
      .map(event => {
        const evStart = new Date(event.startTime);
        // Clamp to week boundaries
        const colStart = weekDates.findIndex(wd => {
          const d = new Date(wd.date);
          d.setHours(0, 0, 0, 0);
          return d >= new Date(evStart.getFullYear(), evStart.getMonth(), evStart.getDate()) || isSameDay(d, evStart);
        });
        const start = Math.max(0, colStart);
        
        let colEnd = 6;
        for (let i = 6; i >= 0; i--) {
          if (eventSpansDate(event, weekDates[i].date)) {
            colEnd = i;
            break;
          }
        }
        const span = colEnd - start + 1;
        
        return { event, start, span };
      })
      .filter(s => s.span > 0);

    // Deduplicate by event id
    const seen = new Set();
    return spanning.filter(s => {
      if (seen.has(s.event.id)) return false;
      seen.add(s.event.id);
      return true;
    });
  }, [events]);

  // Get single-day events for a date (excluding multi-day)
  const getSingleDayEventsForDate = useCallback((date) => {
    return events
      .filter(event => !isMultiDayEvent(event) && eventSpansDate(event, date))
      .sort((a, b) => {
        if (a.allDay && !b.allDay) return -1;
        if (!a.allDay && b.allDay) return 1;
        return new Date(a.startTime) - new Date(b.startTime);
      });
  }, [events]);

  // Render month view
  const renderMonthView = () => (
    <div ref={calendarContainerRef} className="bg-white rounded-xl shadow-sm border overflow-hidden relative">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b bg-gray-50">
        {DAYS.map(day => (
          <div key={day} className="py-3 text-center text-sm font-medium text-gray-600 border-r last:border-r-0">
            {day}
          </div>
        ))}
      </div>
      
      {/* Week rows */}
      {calendarWeeks.map((week, weekIdx) => {
        const spanningEvents = getSpanningEventsForWeek(week);
        
        return (
          <div key={weekIdx} className="border-b last:border-b-0">
            {/* Day number headers */}
            <div className="grid grid-cols-7">
              {week.map((dayData, dayIdx) => {
                const isToday = isSameDay(dayData.date, new Date());
                return (
                  <div
                    key={dayIdx}
                    className={`px-1 pt-1 border-r last:border-r-0 ${!dayData.isCurrentMonth ? 'bg-gray-50' : ''} ${isToday ? 'bg-indigo-50' : ''}`}
                  >
                    <div className={`text-right ${isToday ? 'text-indigo-600 font-bold' : !dayData.isCurrentMonth ? 'text-gray-400' : ''}`}>
                      <span className={`inline-block w-7 h-7 leading-7 text-center text-sm rounded-full ${isToday ? 'bg-indigo-600 text-white' : ''}`}>
                        {dayData.date.getDate()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Spanning multi-day event bars */}
            {spanningEvents.length > 0 && (
              <div className="grid grid-cols-7 px-0">
                {spanningEvents.map(({ event, start, span }) => (
                  <div
                    key={event.id}
                    className="py-0.5 px-0.5"
                    style={{ gridColumn: `${start + 1} / span ${span}` }}
                  >
                    <EventCard
                      event={event}
                      compact
                      spanning
                      spanCols={1}
                      containerRef={calendarContainerRef}
                      onClick={() => {
                        setSelectedEvent(event);
                        setIsNewEvent(false);
                        setShowEventModal(true);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            
            {/* Single-day events per cell */}
            <div className="grid grid-cols-7" style={{ minHeight: '60px' }}>
              {week.map((dayData, dayIdx) => {
                const singleEvents = getSingleDayEventsForDate(dayData.date);
                const isToday = isSameDay(dayData.date, new Date());
                
                return (
                  <div
                    key={dayIdx}
                    className={`px-1 pb-1 border-r last:border-r-0 cursor-pointer hover:bg-indigo-50/50 transition-colors
                      ${!dayData.isCurrentMonth ? 'bg-gray-50' : ''} ${isToday ? 'bg-indigo-50' : ''}`}
                    onClick={() => handleNewEvent(new Date(dayData.date))}
                  >
                    <div className="space-y-0.5">
                      {singleEvents.slice(0, 3).map(event => (
                        <EventCard
                          key={event.id}
                          event={event}
                          compact
                          containerRef={calendarContainerRef}
                          onClick={() => {
                            setSelectedEvent(event);
                            setIsNewEvent(false);
                            setShowEventModal(true);
                          }}
                        />
                      ))}
                      {singleEvents.length > 3 && (
                        <div className="text-xs text-gray-500 px-1">
                          +{singleEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
  
  // Render week view
  const renderWeekView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    // Build week data with date objects for spanning calculation
    const weekData = weekDays.map(d => ({ date: d, isCurrentMonth: true }));
    const weekSpanning = getSpanningEventsForWeek(weekData);
    
    return (
      <div ref={calendarContainerRef} className="bg-white rounded-xl shadow-sm border overflow-hidden relative">
        {/* Day headers */}
        <div className="grid grid-cols-8 border-b bg-gray-50">
          <div className="py-3 px-2 text-center text-sm font-medium text-gray-400 border-r">
            Time
          </div>
          {weekDays.map((date, idx) => {
            const isToday = isSameDay(date, new Date());
            return (
              <div
                key={idx}
                className={`py-3 text-center border-r last:border-r-0 ${isToday ? 'bg-indigo-50' : ''}`}
              >
                <div className="text-xs text-gray-500">{DAYS[date.getDay()]}</div>
                <div className={`text-lg font-medium ${isToday ? 'text-indigo-600' : 'text-gray-700'}`}>
                  {date.getDate()}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Spanning multi-day event bars */}
        {weekSpanning.length > 0 && (
          <div className="border-b bg-gray-50/50">
            {weekSpanning.map(({ event, start, span }) => (
              <div
                key={event.id}
                className="py-0.5 px-0.5"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(60px, auto) repeat(7, 1fr)',
                }}
              >
                <div />{/* Empty spacer for time column */}
                <div style={{ gridColumn: `${start + 2} / span ${span}` }}>
                  <EventCard
                    event={event}
                    compact
                    spanning
                    containerRef={calendarContainerRef}
                    onClick={() => {
                      setSelectedEvent(event);
                      setIsNewEvent(false);
                      setShowEventModal(true);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Time grid */}
        <div className="overflow-y-auto max-h-[600px]">
          {hours.map(hour => (
            <div key={hour} className="grid grid-cols-8 border-b">
              <div className="py-2 px-2 text-xs text-gray-400 border-r text-right">
                {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </div>
              {weekDays.map((date, idx) => {
                // Only show single-day events in the time grid (multi-day shown above)
                const hourEvents = events.filter(e => {
                  if (isMultiDayEvent(e)) return false;
                  if (!eventSpansDate(e, date)) return false;
                  const eventDate = new Date(e.startTime);
                  return eventDate.getHours() === hour;
                });
                
                return (
                  <div
                    key={idx}
                    className="min-h-[50px] border-r last:border-r-0 p-1 hover:bg-indigo-50/50 cursor-pointer transition-colors"
                    onClick={() => {
                      const newDate = new Date(date);
                      newDate.setHours(hour, 0, 0, 0);
                      handleNewEvent(newDate);
                    }}
                  >
                    {hourEvents.map(event => (
                      <EventCard
                        key={event.id}
                        event={event}
                        compact
                        containerRef={calendarContainerRef}
                        onClick={() => {
                          setSelectedEvent(event);
                          setIsNewEvent(false);
                          setShowEventModal(true);
                        }}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Render day view
  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const dayEvents = getEventsForDate(currentDate);
    
    return (
      <div ref={calendarContainerRef} className="bg-white rounded-xl shadow-sm border overflow-hidden relative">
        <div className="grid grid-cols-4 gap-4">
          {/* Time grid */}
          <div className="col-span-3 border-r">
            <div className="overflow-y-auto max-h-[600px]">
              {hours.map(hour => {
                const hourEvents = dayEvents.filter(e => {
                  if (e.allDay) return false;
                  const eventDate = new Date(e.startTime);
                  // Show on start hour, or at hour 0 for continuation days
                  return isSameDay(eventDate, currentDate)
                    ? eventDate.getHours() === hour
                    : hour === 0;
                });
                
                return (
                  <div key={hour} className="flex border-b">
                    <div className="w-20 py-3 px-2 text-xs text-gray-400 text-right border-r flex-shrink-0">
                      {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                    </div>
                    <div
                      className="flex-1 min-h-[60px] p-2 hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        const newDate = new Date(currentDate);
                        newDate.setHours(hour, 0, 0, 0);
                        handleNewEvent(newDate);
                      }}
                    >
                      {hourEvents.map(event => (
                        <EventCard
                          key={event.id}
                          event={event}
                          containerRef={calendarContainerRef}
                          onClick={() => {
                            setSelectedEvent(event);
                            setIsNewEvent(false);
                            setShowEventModal(true);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* All-day events sidebar */}
          <div className="p-4">
            <h3 className="font-medium text-gray-700 mb-3">All Day</h3>
            {dayEvents.filter(e => e.allDay).map(event => (
              <EventCard
                key={event.id}
                event={event}
                containerRef={calendarContainerRef}
                onClick={() => {
                  setSelectedEvent(event);
                  setIsNewEvent(false);
                  setShowEventModal(true);
                }}
              />
            ))}
            {dayEvents.filter(e => e.allDay).length === 0 && (
              <p className="text-sm text-gray-400">No all-day events</p>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Render agenda view
  const renderAgendaView = () => {
    // Get events for the next 30 days, including multi-day spans
    const startDate = new Date(currentDate);
    const endDate = new Date(currentDate);
    endDate.setDate(endDate.getDate() + 30);
    
    // Group by date, expanding multi-day events across each day they span
    const groupedEvents = {};
    const current = new Date(startDate);
    while (current <= endDate) {
      const dayEvents = events.filter(e => eventSpansDate(e, current));
      if (dayEvents.length > 0) {
        const dateKey = new Date(current).toDateString();
        groupedEvents[dateKey] = dayEvents.sort((a, b) => {
          if (a.allDay && !b.allDay) return -1;
          if (!a.allDay && b.allDay) return 1;
          return new Date(a.startTime) - new Date(b.startTime);
        });
      }
      current.setDate(current.getDate() + 1);
    }
    
    return (
      <div ref={calendarContainerRef} className="bg-white rounded-xl shadow-sm border overflow-hidden relative">
        <div className="divide-y">
          {Object.entries(groupedEvents).length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <svg className="h-12 w-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p>No upcoming events in the next 30 days</p>
            </div>
          ) : (
            Object.entries(groupedEvents).map(([dateKey, dayEvents]) => {
              const date = new Date(dateKey);
              const isToday = isSameDay(date, new Date());
              
              return (
                <div key={dateKey} className={`${isToday ? 'bg-indigo-50' : ''}`}>
                  <div className="px-4 py-2 bg-gray-50 border-b sticky top-0">
                    <span className={`font-medium ${isToday ? 'text-indigo-600' : 'text-gray-700'}`}>
                      {isToday ? 'Today' : FULL_DAYS[date.getDay()]}
                    </span>
                    <span className="text-gray-500 ml-2">
                      {formatDate(date)}
                    </span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {dayEvents.map(event => (
                      <div
                        key={event.id}
                        className="px-4 py-3 hover:bg-indigo-50 cursor-pointer flex items-start gap-4 transition-colors"
                        onClick={() => {
                          setSelectedEvent(event);
                          setIsNewEvent(false);
                          setShowEventModal(true);
                        }}
                      >
                        <div className="w-24 text-sm text-gray-500 flex-shrink-0">
                          {event.allDay ? 'All day' : formatTimeRange(event)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: EVENT_TYPES[event.eventType]?.color || '#6366F1' }}
                            />
                            <span className="font-medium text-gray-900 truncate">{event.title}</span>
                          </div>
                          {event.location && (
                            <p className="text-sm text-gray-500 mt-0.5 truncate">📍 {event.location}</p>
                          )}
                          {isAdmin && event.userDisplayName && event.userId !== currentUser.uid && (
                            <p className="text-xs text-gray-400 mt-0.5">👤 {event.userDisplayName}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="p-4 md:p-6 max-w-full">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            📅 Sales Rep Calendar
          </h1>
          <p className="text-sm text-gray-500 mt-1">Track trade shows, market weeks, customer visits, and more</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* New event button */}
          {can(FEATURE_PERMISSIONS.CALENDAR_CREATE) && (
          <button
            onClick={() => handleNewEvent()}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Event
          </button>
          )}
        </div>
      </div>
      
      {/* Filters and Navigation */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        {/* Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigatePeriod(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Previous"
          >
            <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Today
          </button>
          
          <button
            onClick={() => navigatePeriod(1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Next"
          >
            <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          <h2 className="text-lg font-semibold text-gray-800 ml-2">
            {getPeriodTitle()}
          </h2>
        </div>
        
        {/* View mode and filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Event type filter */}
          <select
            value={filterEventType}
            onChange={(e) => setFilterEventType(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Event Types</option>
            {Object.entries(EVENT_TYPES).map(([key, config]) => (
              <option key={key} value={key}>{config.icon} {config.name}</option>
            ))}
          </select>
          
          {/* User filter (admin only) */}
          {isAdmin && (
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Users</option>
              <option value={currentUser.uid}>My Calendar</option>
              {users.filter(u => u.id !== currentUser.uid && u.role !== 'disabled').map(user => (
                <option key={user.id} value={user.id}>
                  {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                </option>
              ))}
            </select>
          )}
          
          {/* View mode toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {Object.entries(VIEW_MODES).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setViewMode(value)}
                className={`px-3 py-2 text-sm ${viewMode === value 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'} border-r last:border-r-0`}
              >
                {key.charAt(0) + key.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Calendar View */}
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <>
          {viewMode === VIEW_MODES.MONTH && renderMonthView()}
          {viewMode === VIEW_MODES.WEEK && renderWeekView()}
          {viewMode === VIEW_MODES.DAY && renderDayView()}
          {viewMode === VIEW_MODES.AGENDA && renderAgendaView()}
        </>
      )}
      
      {/* Event Modal */}
      {showEventModal && (
        <EventModal
          event={selectedEvent}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onSave={can(FEATURE_PERMISSIONS.CALENDAR_EDIT) ? handleSaveEvent : null}
          onDelete={can(FEATURE_PERMISSIONS.CALENDAR_DELETE) ? handleDeleteEvent : null}
          isNew={isNewEvent}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};

export default CalendarPage;
